import { useEffect, useState } from "react";
import { cachedGet } from "../lib/sharedData";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { ClientClausesTab } from "./ClausesPage";
import { PortalAccessPanel, PortalShareToggle } from "../components/portal/PortalAgentParts";
import { api } from "../lib/api";
import { fmtMoney, fmtDate, fmtDateTime, formatMarketValue, fmtRelativeDays } from "../lib/format";
import { Wedge } from "../components/wedges/Wedge";
import { TransferRoomCard } from "../components/wedges/TransferRoomCard";
import { WedgeDrawer } from "../components/wedges/WedgeDrawer";
import TierBadge from "../components/TierBadge";
import MandateWizard from "../components/MandateWizard";
import MandateDetailDrawer from "../components/MandateDetailDrawer";
import ReportPickerModal from "../components/ReportPickerModal";
import { ClientNotesSection } from "./NotesGlobal";
import { Key as KeyIcon, Plus as PlusIcon } from "lucide-react";
import {
  MessageSquare, PhoneCall, UploadCloud, FilePlus, Cake, FileSignature,
  Calendar, Wallet, ChevronRight, TrendingUp, TrendingDown, Plus, Activity, Trophy,
  FileText, Download, Send,
} from "lucide-react";
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, Legend,
} from "recharts";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "contracts", label: "Contracts" },
  { id: "documents", label: "Documents" },
  { id: "stats", label: "Value & Stats" },
  { id: "cases", label: "Cases" },
  { id: "schedule", label: "Schedule" },
  { id: "payroll", label: "Payroll" },
  { id: "clauses", label: "Clauses" },
  { id: "portal", label: "Player portal" },
];

const PRIORITY_TONE = {
  low: "pm-pill-slate", medium: "pm-pill-blue", high: "pm-pill-amber", urgent: "pm-pill-red",
};
const STATUS_TONE = {
  open: "pm-pill-green", pending: "pm-pill-amber", resolved: "pm-pill-slate",
};
const DOC_TONE = {
  player_contract: "pm-pill-green", representation: "pm-pill-blue",
  loan: "pm-pill-amber", image_rights: "pm-pill-slate", nda: "pm-pill-slate", other: "pm-pill-slate",
};

export default function ClientHomepage() {
  const { id } = useParams();
  const [search, setSearch] = useSearchParams();
  const tab = search.get("tab") || "overview";

  const [overview, setOverview] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [legalCases, setLegalCases] = useState([]);
  const [events, setEvents] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [payrollSum, setPayrollSum] = useState(null);
  const [completion, setCompletion] = useState(null);
  // Wedge expanded-view drawer
  const [drawerProvider, setDrawerProvider] = useState(null);

  useEffect(() => {
    api.get(`/clients/${id}/overview`).then((r) => setOverview(r.data)).catch(() => {});
    api.get(`/clients/${id}/contracts`).then((r) => setContracts(r.data || []));
    api.get(`/clients/${id}/stats`).then((r) => setStats(r.data)).catch(() => {});
    api.get(`/cases?client_id=${id}`).then((r) => setCases(r.data || []));
    api.get(`/legal/cases?client_id=${id}`).then((r) => setLegalCases(r.data || [])).catch(() => setLegalCases([]));
    api.get(`/clients/${id}/events`).then((r) => setEvents(r.data || []));
    api.get(`/clients/${id}/payroll`).then((r) => setPayroll(r.data || []));
    api.get(`/clients/${id}/payroll/summary`).then((r) => setPayrollSum(r.data));
    api.get(`/clients/${id}/completion`).then((r) => setCompletion(r.data)).catch(() => {});
  }, [id]);

  const c = overview?.client;
  const setTab = (id) => setSearch({ tab: id });

  if (!c) {
    return <Layout title="Loading client…" />;
  }

  return (
    <Layout
      title={c.full_name}
      subtitle={`${c.position} · ${c.current_club}`}
      right={
        <Link to="/clients" className="pm-btn-secondary px-4 h-10 rounded-xl text-sm font-bold flex items-center" data-testid="back-to-clients-btn">
          Back to roster
        </Link>
      }
    >
      {/* Hero briefing card */}
      <BriefingCard overview={overview} />

      <TierStatusBanner client={c} />

      <ClientMandateSection client={c} />

      <ClientReportsSection clientId={id} clientName={c.full_name} />

      <AssignedAgentChip
        clientId={id}
        client={c}
        onReassigned={() => api.get(`/clients/${id}/overview`).then((r) => setOverview(r.data)).catch(() => {})}
      />

      {/* Profile completion ribbon */}
      {completion && completion.completion_pct < 100 && (
        <div className="mt-4 pm-card p-4 flex items-center gap-4" data-testid="completion-ribbon">
          <div className="relative w-12 h-12 shrink-0">
            <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#00E676" strokeWidth="3"
                      strokeDasharray={`${completion.completion_pct}, 100`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-mono-tab font-bold text-xs">{completion.completion_pct}%</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm text-slate-900">Profile completion</div>
            <div className="text-xs text-slate-500 truncate">
              {completion.missing.length} field{completion.missing.length === 1 ? "" : "s"} pending · {completion.missing.slice(0, 4).join(", ")}{completion.missing.length > 4 ? "…" : ""}
            </div>
          </div>
        </div>
      )}

      {/* Quick actions toolbar */}
      <div className="mt-6 flex items-center justify-center" data-testid="quick-actions-toolbar">
        <div className="pm-glass rounded-full px-2 py-2 inline-flex items-center gap-1 shadow-sm">
          <QuickAction icon={MessageSquare} label="Message client" testid="qa-message" />
          <QuickAction icon={PhoneCall} label="Log call" testid="qa-log-call" />
          <QuickAction icon={UploadCloud} label="Upload doc" testid="qa-upload" />
          <QuickAction icon={FilePlus} label="Create case" testid="qa-create-case" primary />
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-8 border-b border-slate-200 flex items-center gap-1 overflow-x-auto pm-scroll" data-testid="client-tabs">
        {TABS.filter((t) => {
          if ((c.tier || "client") === "client") return true;
          // Network + Mandated tiers hide Contracts, Cases, Payroll
          return !["contracts", "cases", "payroll"].includes(t.id);
        }).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-4 py-3 text-sm font-bold tracking-tight whitespace-nowrap border-b-2 transition-all duration-200 ${
              tab === t.id
                ? "border-emerald-500 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 pm-fade">
        {tab === "overview" && <OverviewTab overview={overview} stats={stats} cases={cases} legalCases={legalCases} payrollSum={payrollSum} clientId={id} client={c} onExpand={setDrawerProvider} />}
        {tab === "contracts" && <ContractsTab contracts={contracts} onShared={(cid, r) => setContracts((xs) => xs.map((x) => (x.id === cid ? { ...x, ...r } : x)))} />}
        {tab === "documents" && <DocumentsTab clientId={id} clientName={c.full_name} />}
        {tab === "stats" && <StatsTab stats={stats} client={c} clientId={id} onExpand={setDrawerProvider} />}
        {tab === "cases" && <CasesTab cases={cases} legalCases={legalCases} clientId={id} />}
        {tab === "schedule" && <ScheduleTab events={events} />}
        {tab === "payroll" && <PayrollTab payroll={payroll} summary={payrollSum} currency={c.currency} />}
        {tab === "clauses" && <ClientClausesTab clientId={id} />}
        {tab === "portal" && <PortalAccessPanel clientId={id} />}
      </div>
      <WedgeDrawer
        key={drawerProvider || "closed"}
        open={!!drawerProvider}
        provider={drawerProvider}
        clientId={id}
        onClose={() => setDrawerProvider(null)}
      />
    </Layout>
  );
}

/* ----------------- Briefing card ----------------- */
function BriefingCard({ overview }) {
  const c = overview?.client;
  if (!c) return null;
  const ce = overview.days_to_contract_end;
  const bd = overview.days_to_birthday;
  const dsContact = overview.days_since_contact;
  const dsVisit = overview.days_since_visit;
  const healthDot =
    dsContact == null ? { color: "bg-slate-300", label: "No contact logged" }
    : dsContact <= 14 ? { color: "bg-emerald-500", label: `Last contact ${dsContact}d ago` }
    : dsContact <= 30 ? { color: "bg-amber-500", label: `Last contact ${dsContact}d ago — getting cold` }
    : { color: "bg-red-500", label: `Last contact ${dsContact}d ago — relationship stale` };
  const visitDot =
    dsVisit == null ? { color: "bg-slate-300", label: "No match visit logged" }
    : dsVisit <= 30 ? { color: "bg-emerald-500", label: `Visited ${dsVisit}d ago` }
    : dsVisit <= 60 ? { color: "bg-amber-500", label: `Last visit ${dsVisit}d ago` }
    : { color: "bg-red-500", label: `Last visit ${dsVisit}d ago — overdue` };
  return (
    <div className="pm-card p-6 lg:p-8 relative overflow-hidden" data-testid="briefing-card">
      <div aria-hidden className="absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-30"
           style={{ background: "radial-gradient(circle, rgba(0,230,118,0.35), transparent 70%)" }} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative">
        <div className="lg:col-span-3">
          <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-gradient-to-br from-slate-700 to-slate-900 ring-1 ring-slate-200">
            {c.photo_url ? (
              <img src={c.photo_url} alt={c.full_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center" data-testid="client-avatar-initials">
                <span className="font-display font-black text-white text-5xl tracking-tight select-none">
                  {(c.full_name || "").split(" ").map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="lg:col-span-9">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-2xl">{c.nationality_flag}</span>
            <span className="text-xs uppercase tracking-widest font-bold text-slate-500">{c.nationality}</span>
            <span className="pm-pill pm-pill-green">Active client</span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-600" title={healthDot.label} data-testid="briefing-health-dot">
              <span className={`w-2 h-2 rounded-full ${healthDot.color}`} /> Health
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-600" title={visitDot.label} data-testid="briefing-visit-dot">
              <span className={`w-2 h-2 rounded-full ${visitDot.color}`} /> Visit {dsVisit != null ? `${dsVisit}d` : "—"}
            </span>
          </div>
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-2 leading-none">
            {c.full_name}
          </h2>
          <div className="text-slate-500 mt-2">{c.position} · {c.current_club}</div>
          {c.bio && <p className="text-sm text-slate-600 mt-3 leading-relaxed max-w-3xl">{c.bio}</p>}

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
            <Metric label="Age" value={overview.age ?? "—"} sub={bd != null ? `${bd}d to birthday` : null} icon={Cake} />
            <Metric
              label="Market value"
              value={formatMarketValue(c.market_value_eur)}
              sub={
                c.market_value_eur && c.market_value_fetched_at
                  ? `via Transfermarkt · updated ${fmtRelativeDays(c.market_value_fetched_at)}`
                  : "Transfermarkt"
              }
              trend={c.market_value_eur ? "up" : null}
              testid="homepage-market-value"
            />
            <Metric label="Weekly wage" value={c.weekly_wage ? fmtMoney(c.weekly_wage, c.currency) : "—"} sub={c.monthly_wage ? `${fmtMoney(c.monthly_wage, c.currency)}/mo` : null} />
            <Metric label="Contract ends" value={c.contract_end ? fmtDate(c.contract_end) : "—"} sub={ce != null ? `${ce}d remaining` : null} tone={ce != null && ce <= 180 ? "danger" : ce != null && ce <= 365 ? "warn" : "ok"} />
            <Metric label="Agent commission" value={c.agent_commission_rate != null ? `${c.agent_commission_rate}%` : "—"} sub={c.agent_commission_rate != null ? "of gross" : null} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, sub, icon: Icon, trend, tone, testid }) {
  const toneColor = tone === "danger" ? "text-red-600" : tone === "warn" ? "text-amber-600" : "text-slate-900";
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 flex items-center gap-1">
        {Icon && <Icon size={11} />}
        {label}
      </div>
      <div className={`font-display font-black text-xl font-mono-tab mt-1 ${toneColor}`}>{value}</div>
      {sub && (
        <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
          {trend === "up" && <TrendingUp size={11} className="text-emerald-500" />}
          {trend === "down" && <TrendingDown size={11} className="text-red-500" />}
          {sub}
        </div>
      )}
    </div>
  );
}

function QuickAction({ icon: Icon, label, testid, primary }) {
  return (
    <button
      data-testid={testid}
      className={`flex items-center gap-2 px-4 h-10 rounded-full text-sm font-bold transition-all duration-200 ${
        primary
          ? "bg-[#00E676] text-[#062E17] hover:bg-[#00C863]"
          : "text-slate-700 hover:bg-white/80"
      }`}
    >
      <Icon size={14} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

/* ----------------- Overview tab ----------------- */
function OverviewTab({ overview, stats, cases, legalCases, payrollSum, clientId, client, onExpand }) {
  const activity = overview?.recent_activity || [];
  const next = overview?.next_event;
  const openCases = (cases || []).filter((x) => x.status !== "resolved").slice(0, 5);
  const trackAffiliate = (provider) => {
    api.post("/integrations/affiliate-click", { provider, placement: "client_overview_wedge" }).catch(() => {});
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        {/* Integration wedges */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="wedges-grid-overview">
          <Wedge provider="api_football" clientId={clientId} onAffiliateClick={trackAffiliate} onExpand={onExpand} />
          <Wedge provider="contract" clientId={clientId} onAffiliateClick={trackAffiliate} onExpand={onExpand} />
          <Wedge provider="email" clientId={clientId} onAffiliateClick={trackAffiliate} onExpand={onExpand} />
          <Wedge provider="whatsapp_sms" clientId={clientId} onAffiliateClick={trackAffiliate} onExpand={onExpand} />
          <div className="md:col-span-2">
            <Wedge provider="legal" clientId={clientId} onAffiliateClick={trackAffiliate} onExpand={onExpand} />
          </div>
        </div>
        {/* Next event */}
        <div className="pm-card p-6" data-testid="overview-next-event">
          <div className="flex items-center gap-2 mb-1">
            <Calendar size={14} className="text-emerald-500" />
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Next on calendar</span>
          </div>
          {next ? (
            <div className="flex items-center justify-between gap-4 mt-2">
              <div>
                <div className="font-display text-2xl font-bold tracking-tight">{next.title}</div>
                <div className="text-sm text-slate-500 mt-1">{next.location || "—"}</div>
              </div>
              <div className="text-right">
                <div className="font-mono-tab text-slate-900 font-bold">{fmtDateTime(next.start)}</div>
                <span className="pm-pill pm-pill-blue mt-1 inline-block">{(next.event_type || "event").replace("_", " ")}</span>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500 mt-2">No events scheduled.</div>
          )}
        </div>

        {/* Open cases */}
        <div className="pm-card p-6" data-testid="overview-open-cases">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Active threads</div>
              <h3 className="font-display text-2xl font-bold tracking-tight">Open cases</h3>
            </div>
            <span className="pm-pill pm-pill-slate">{openCases.length}</span>
          </div>
          {openCases.length === 0 && <div className="text-sm text-slate-500">No open cases.</div>}
          <div className="space-y-2">
            {openCases.map((cs) => (
              <Link key={cs.id} to={`/cases/${cs.id}`}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-200"
                    data-testid={`overview-case-${cs.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{cs.title}</div>
                  <div className="text-xs text-slate-500 truncate">{(cs.parties || []).join(" · ")}</div>
                </div>
                <span className={`pm-pill ${PRIORITY_TONE[cs.priority]}`}>{cs.priority}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Legal cases */}
        {(legalCases || []).length > 0 && (
          <div className="pm-card p-6" data-testid="overview-legal-cases">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Legal hub</div>
                <h3 className="font-display text-2xl font-bold tracking-tight">Legal cases</h3>
              </div>
              <Link to="/legal" className="pm-pill pm-pill-slate hover:bg-slate-200" data-testid="overview-legal-link">
                {legalCases.length}
              </Link>
            </div>
            <div className="space-y-2">
              {legalCases.slice(0, 5).map((lc) => (
                <Link
                  key={lc.id}
                  to="/legal"
                  className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-200"
                  data-testid={`overview-legal-case-${lc.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-mono-tab text-[11px] text-slate-500">{lc.case_number}</span>
                      <span className="pm-pill pm-pill-slate text-[10px]">{(lc.case_type || "regulatory").replace(/_/g, " ")}</span>
                    </div>
                    <div className="font-semibold text-slate-900 truncate">{lc.summary || "—"}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {lc.jurisdiction || "—"} · vs {lc.opposing_party || "—"} · {lc.governing_body || "—"}
                    </div>
                  </div>
                  <span className={`pm-pill ${
                    lc.status === "resolved" ? "pm-pill-green" :
                    lc.status === "hearing_scheduled" || lc.status === "appealed" ? "pm-pill-red" :
                    lc.status === "awaiting_ruling" ? "pm-pill-amber" :
                    "pm-pill-slate"
                  }`}>
                    {(lc.status || "draft").replace(/_/g, " ")}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Mini market value */}
        {stats?.market_history?.length > 0 && (
          <div className="pm-card p-6" data-testid="overview-market-mini">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Transfermarkt</div>
                <h3 className="font-display text-2xl font-bold tracking-tight">Market value (10 months)</h3>
              </div>
              <span className="pm-pill pm-pill-green">Live sync</span>
            </div>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.market_history} margin={{ left: 0, right: 0, top: 5, bottom: 0 }}>
                  <defs>
                    <linearGradient id="mvFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00E676" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#00E676" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(v) => v?.slice(0, 7)} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} unit="M" />
                  <Tooltip formatter={(v) => `€${v}M`} contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                  <Area type="monotone" dataKey="value" stroke="#00C863" strokeWidth={2} fill="url(#mvFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Right column: activity + financial summary */}
      <div className="space-y-5">
        <Wedge provider="transfermarkt" clientId={clientId} onAffiliateClick={trackAffiliate} onExpand={onExpand} />
        <ClientNotesSection clientId={clientId} clientName={client?.full_name} />
        <div className="pm-card p-6" data-testid="overview-activity">
          <div className="flex items-center gap-2 mb-4">
            <Activity size={14} className="text-emerald-500" />
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Recent activity</div>
          </div>
          <div className="space-y-3">
            {activity.length === 0 && <div className="text-sm text-slate-500">No recent activity.</div>}
            {activity.map((a) => (
              <div key={a.id} className="flex gap-3">
                <div className="pm-dot mt-2" style={{ background: "var(--pm-green)" }} />
                <div className="flex-1">
                  <div className="text-sm text-slate-800">
                    <span className="font-semibold">{a.actor}</span> {a.action}
                    {a.target && <> — <span className="text-slate-600">{a.target}</span></>}
                  </div>
                  <div className="text-xs text-slate-400 font-mono-tab mt-0.5">{fmtDate(a.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {payrollSum && (
          <div className="pm-card p-6" data-testid="overview-financial-summary">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Career financial snapshot</div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Metric label="Total gross" value={fmtMoney(payrollSum.total_gross_eur)} />
              <Metric label="Total net" value={fmtMoney(payrollSum.total_net_eur)} />
              <Metric label="Agent commission" value={fmtMoney(payrollSum.total_commission)} />
              <Metric label="Bonuses" value={fmtMoney(payrollSum.total_bonuses)} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ----------------- Contracts tab ----------------- */
function ContractsTab({ contracts, onShared }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold tracking-tight">All contracts ({contracts.length})</h3>
        <button className="pm-btn-primary h-10 px-4 rounded-xl text-sm flex items-center gap-2" data-testid="upload-contract-btn">
          <Plus size={14} /> Upload contract
        </button>
      </div>

      <div className="space-y-3">
        {contracts.map((c) => (
          <div key={c.id} className="pm-card p-5" data-testid={`contract-card-${c.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <FileSignature size={14} className="text-slate-400" />
                  <span className={`pm-pill ${DOC_TONE[c.doc_type] || "pm-pill-slate"}`}>{c.doc_type.replace("_", " ")}</span>
                  <span className={`pm-pill ${c.status === "active" ? "pm-pill-green" : c.status === "in_negotiation" ? "pm-pill-amber" : "pm-pill-slate"}`}>{c.status.replace("_", " ")}</span>
                  <span className="text-xs text-slate-400 font-mono-tab">v{c.version}</span>
                </div>
                <div className="font-display font-bold text-lg tracking-tight">{c.title}</div>
                <div className="text-sm text-slate-500">{c.counterparty}</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <Metric label="Effective" value={fmtDate(c.effective_date)} />
                  <Metric label="Expires" value={fmtDate(c.expiry_date)} />
                  <Metric label="Base wage" value={c.base_wage ? fmtMoney(c.base_wage, c.currency) : "—"} />
                  <Metric label="Bonuses" value={`${(c.bonuses || []).length} clauses`} />
                </div>
                {(c.bonuses || []).length > 0 && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2">
                    {c.bonuses.map((b, i) => (
                      <div key={i} className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-xs">
                        <div className="font-bold text-slate-800">{b.label}</div>
                        <div className="text-slate-500 mt-0.5 font-mono-tab">{typeof b.amount === "number" ? b.amount : b.amount} · {b.trigger}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                {c.file_name && <div className="text-xs text-slate-500 font-mono-tab whitespace-nowrap">{c.file_name}</div>}
                <PortalShareToggle kind="contracts" id={c.id} visible={!!c.portal_visible} onChange={(r) => onShared?.(c.id, r)} compact />
              </div>
            </div>
          </div>
        ))}
        {contracts.length === 0 && <div className="text-sm text-slate-500">No contracts on file yet.</div>}
      </div>
    </div>
  );
}

/* ----------------- Documents tab ----------------- */
function DocumentsTab({ clientId, clientName }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [flash, setFlash] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/clients/${clientId}/documents`)
      .then((r) => setDocs(r.data?.documents || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [clientId]);

  const download = async (d) => {
    try {
      setBusy(d.id);
      const { data: signed } = await api.post(`/clients/${clientId}/documents/${d.id}/download-url`);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${signed.url}`);
      if (!r.ok) throw new Error("download failed");
      const blob = await r.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank", "noopener");
    } catch {
      setFlash("Could not download document");
      setTimeout(() => setFlash(""), 2200);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title || d.filename}" from ${clientName}'s file?`)) return;
    try {
      setBusy(d.id);
      await api.delete(`/clients/${clientId}/documents/${d.id}`);
      setDocs((xs) => xs.filter((x) => x.id !== d.id));
      setFlash("Document deleted");
      setTimeout(() => setFlash(""), 2200);
    } catch {
      setFlash("Could not delete document");
      setTimeout(() => setFlash(""), 2200);
    } finally {
      setBusy(null);
    }
  };

  const humanSize = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4" data-testid="client-documents-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold tracking-tight">
          Documents <span className="text-slate-400 font-mono-tab text-base">({docs.length})</span>
        </h3>
        <Link
          to="/inbox"
          className="text-xs font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
          data-testid="documents-from-inbox-link"
        >
          Save more from Inbox <ChevronRight size={12} />
        </Link>
      </div>

      {flash && (
        <div className="text-[12px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block" data-testid="documents-flash">
          {flash}
        </div>
      )}

      {loading && <div className="text-sm text-slate-500">Loading documents…</div>}

      {!loading && docs.length === 0 && (
        <div className="pm-card p-8 text-center" data-testid="documents-empty">
          <FileText size={28} className="mx-auto text-slate-300 mb-2" />
          <div className="font-bold text-slate-700">No documents linked to {clientName} yet.</div>
          <div className="text-xs text-slate-500 mt-1">
            Save attachments from the Inbox using <b>Docs → Save to scouting docs</b>, or upload from this page.
          </div>
        </div>
      )}

      {!loading && docs.length > 0 && (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} className="pm-card p-4 flex items-center gap-3" data-testid={`document-row-${d.id}`}>
              <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-700 shrink-0">
                <FileText size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-900 truncate" title={d.title}>{d.title || d.filename}</div>
                <div className="text-[11px] text-slate-500 font-mono-tab flex items-center gap-2 flex-wrap mt-0.5">
                  <span>{d.filename}</span>
                  <span>·</span>
                  <span>{(d.mime || "").split("/")[1]?.toUpperCase() || "FILE"}</span>
                  <span>·</span>
                  <span>{humanSize(d.size)}</span>
                  <span>·</span>
                  <span>{fmtDate(d.created_at)}</span>
                  {d.source === "email" && (
                    <span className="pm-pill pm-pill-blue text-[10px]">from email</span>
                  )}
                  {(d.tags || []).filter((t) => t !== "from-email").map((t) => (
                    <span key={t} className="pm-pill pm-pill-slate text-[10px]">{t}</span>
                  ))}
                </div>
                {d.source_from && (
                  <div className="text-[11px] text-slate-500 italic mt-0.5 truncate">
                    From: {d.source_from} · uploaded by {d.uploaded_by_email || "—"}
                  </div>
                )}
              </div>
              <PortalShareToggle kind="documents" id={d.id} visible={!!d.portal_visible} group={d.portal_group} onChange={(r) => setDocs((xs) => xs.map((x) => (x.id === d.id ? { ...x, ...r } : x)))} compact />
              <button
                onClick={() => download(d)}
                disabled={busy === d.id}
                className="h-9 px-3 rounded-lg flex items-center gap-1.5 text-xs font-bold text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200 disabled:opacity-50"
                data-testid={`document-download-${d.id}`}
                title="Download"
              >
                <Download size={12} /> Download
              </button>
              <button
                onClick={() => remove(d)}
                disabled={busy === d.id}
                className="h-9 w-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                data-testid={`document-delete-${d.id}`}
                title="Delete"
              >
                <span aria-hidden>×</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------- Stats tab ----------------- */
function StatsTab({ stats, client, clientId, onExpand }) {
  const [showCompare, setShowCompare] = useState(false);
  const trackAffiliate = (provider) => {
    api.post("/integrations/affiliate-click", { provider, placement: "client_stats_wedge" }).catch(() => {});
  };
  if (!stats) return <div className="text-sm text-slate-500">Loading stats…</div>;
  // Some clients (e.g. unscouted prospects) have no Wyscout/SciSports data yet.
  // Render the wedge grid (TransferRoom deep-link still works) + a soft empty state.
  const hasSeasons = Array.isArray(stats.seasons) && stats.seasons.length > 0;
  return (
    <div>
      <div className="flex items-center justify-end gap-2 mb-3">
        <button onClick={() => setShowCompare(true)} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold"
                data-testid="open-compare-btn">Compare players</button>
        <a target="_blank" rel="noreferrer" href={`/clients/${client.id}/scout-pdf?via=react`}
           onClick={(e) => { e.preventDefault(); window.open(`/clients/${client.id}/scout-pdf`, "_blank"); }}
           className="pm-btn-primary h-9 px-3 rounded-lg text-xs font-bold" data-testid="export-scout-pdf">
          Export scouting PDF
        </a>
      </div>
      {/* Live integration wedges — Wyscout · SciSports · Transfermarkt · TransferRoom (deep link) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5" data-testid="wedges-grid-stats">
        <Wedge provider="wyscout" clientId={clientId} onAffiliateClick={trackAffiliate} onExpand={onExpand} />
        <Wedge provider="scisports" clientId={clientId} onAffiliateClick={trackAffiliate} onExpand={onExpand} />
        <Wedge provider="transfermarkt" clientId={clientId} onAffiliateClick={trackAffiliate} onExpand={onExpand} />
        <TransferRoomCard client={client} onAffiliateClick={trackAffiliate} />
      </div>
      {!hasSeasons && (
        <div className="pm-card p-6 text-center" data-testid="stats-empty-state">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
            No scouted stats yet
          </div>
          <div className="font-display text-xl font-bold tracking-tight mt-2">
            {client?.full_name} hasn&rsquo;t been scouted into Wyscout or SciSports.
          </div>
          <div className="text-sm text-slate-500 mt-3 max-w-md mx-auto">
            Once you connect a Wyscout subscription or scout this player into SciSports,
            seasonal output, percentile radar and market-value timeline will appear here.
          </div>
        </div>
      )}
      {hasSeasons && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 pm-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Wyscout · seasonal</div>
            <h3 className="font-display text-2xl font-bold tracking-tight">Output per season</h3>
          </div>
          <span className="pm-pill pm-pill-green">Rating {stats.wyscout_rating}</span>
        </div>
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={stats.seasons}>
              <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="season" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="goals" fill="#00E676" radius={[4,4,0,0]} />
              <Bar dataKey="assists" fill="#0A1128" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-6">
          {stats.seasons.map((s) => (
            <div key={s.season} className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{s.season}</div>
              <div className="text-xs text-slate-600 font-mono-tab mt-1">
                <div>{s.appearances} apps</div>
                <div>{s.goals}G · {s.assists}A</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="pm-card p-6" data-testid="scisports-radar">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">SciSports</div>
            <h3 className="font-display text-2xl font-bold tracking-tight">Player profile</h3>
          </div>
          <span className="pm-pill pm-pill-blue">{stats.scisports_percentile}th pct</span>
        </div>
        <div className="h-72">
          <ResponsiveContainer>
            <RadarChart data={stats.radar} outerRadius="75%">
              <PolarGrid stroke="#cbd5e1" />
              <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11, fill: "#475569" }} />
              <PolarRadiusAxis tick={false} axisLine={false} />
              <Radar dataKey="value" stroke="#00C863" fill="#00E676" fillOpacity={0.35} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="lg:col-span-2 pm-card p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Transfermarkt</div>
            <h3 className="font-display text-2xl font-bold tracking-tight">Market value timeline</h3>
          </div>
          <span className="pm-pill pm-pill-green">{formatMarketValue(client.market_value_eur)}</span>
        </div>
        <div className="h-64">
          <ResponsiveContainer>
            <LineChart data={stats.market_history}>
              <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(v) => v?.slice(0, 7)} />
              <YAxis tick={{ fontSize: 10, fill: "#64748B" }} unit="M" />
              <Tooltip formatter={(v) => `€${v}M`} contentStyle={{ borderRadius: 10, fontSize: 12 }} />
              <Line type="monotone" dataKey="value" stroke="#0A1128" strokeWidth={2.5} dot={{ fill: "#00E676", r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="pm-card p-6">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Transfer history</div>
        <div className="mt-3 space-y-3">
          {(stats.transfers || []).map((t, i) => (
            <div key={i} className="flex items-center justify-between text-sm">
              <div className="flex-1">
                <div className="font-bold text-slate-900">{t.from_club} → {t.to_club}</div>
                <div className="text-xs text-slate-500 font-mono-tab">{t.date}</div>
              </div>
              <div className="font-mono-tab font-bold text-slate-800">€{t.fee_eur_m}M</div>
            </div>
          ))}
        </div>
      </div>
      </div>
      )}
      {showCompare && <CompareModal anchorClientId={client.id} onClose={() => setShowCompare(false)} />}
    </div>
  );
}

function CompareModal({ anchorClientId, onClose }) {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState([anchorClientId]);
  const [compare, setCompare] = useState([]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data || []));
  }, []);
  useEffect(() => {
    if (selected.length < 2) { setCompare([]); return; }
    api.get(`/stats/compare?ids=${selected.join(",")}`).then((r) => setCompare(r.data || []));
  }, [selected]);
  const toggle = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/40 backdrop-blur-sm"
         onClick={onClose} data-testid="compare-modal">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto pm-scroll"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Comparison tool</div>
            <div className="font-display font-black text-xl tracking-tight">Side-by-side overlay (max 3)</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="px-6 py-4 border-b border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Pick players to overlay</div>
          <div className="flex flex-wrap gap-2">
            {clients.map((c) => {
              const on = selected.includes(c.id);
              return (
                <button key={c.id} onClick={() => toggle(c.id)} data-testid={`compare-toggle-${c.id}`}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border-2 text-xs font-bold ${
                          on ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                        }`}>
                  {c.photo_url ? (
                    <img src={c.photo_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                  ) : (
                    <span className="w-5 h-5 rounded-full bg-slate-700 text-white text-[9px] font-bold flex items-center justify-center">
                      {(c.full_name || "").split(" ").map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}
                    </span>
                  )}
                  {c.full_name}
                </button>
              );
            })}
          </div>
        </div>
        <div className="px-6 py-4">
          {compare.length < 2 ? (
            <div className="text-sm text-slate-500 text-center py-8">Pick at least two players above to overlay.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {compare.map((row) => (
                <div key={row.client.id} className="rounded-xl border border-slate-200 p-4" data-testid={`compare-col-${row.client.id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    {row.client.photo_url ? (
                      <img src={row.client.photo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <span className="w-10 h-10 rounded-lg bg-slate-700 text-white text-sm font-bold flex items-center justify-center">
                        {(row.client.full_name || "").split(" ").map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}
                      </span>
                    )}
                    <div className="min-w-0">
                      <div className="font-display font-bold text-sm leading-tight truncate">{row.client.full_name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{row.client.position} · {row.client.current_club}</div>
                    </div>
                  </div>
                  <Stat row label="Market value" value={formatMarketValue(row.client.market_value_eur)} />
                  <Stat row label="Weekly wage" value={fmtMoney(row.client.weekly_wage, "EUR")} />
                  <Stat row label="Wyscout rating" value={row.stats.wyscout_rating} />
                  <Stat row label="SciSports pct" value={`${row.stats.scisports_percentile}th`} />
                  <div className="mt-3 text-[10px] uppercase tracking-widest font-bold text-slate-500">Last season</div>
                  {(() => {
                    const last = row.stats.seasons[row.stats.seasons.length - 1] || {};
                    return (
                      <div className="grid grid-cols-3 gap-2 mt-2 text-xs font-mono-tab">
                        <div><div className="text-[10px] text-slate-500">Apps</div><div className="font-bold">{last.appearances}</div></div>
                        <div><div className="text-[10px] text-slate-500">Goals</div><div className="font-bold">{last.goals}</div></div>
                        <div><div className="text-[10px] text-slate-500">Assists</div><div className="font-bold">{last.assists}</div></div>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-slate-100 last:border-0 text-xs">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className="font-mono-tab font-bold">{value}</div>
    </div>
  );
}

/* ----------------- Cases tab ----------------- */
function CasesTab({ cases, legalCases, clientId }) {
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold tracking-tight">Cases ({cases.length})</h3>
          <button className="pm-btn-primary h-10 px-4 rounded-xl text-sm flex items-center gap-2" data-testid="new-case-btn">
            <Plus size={14} /> New case
          </button>
        </div>
        {cases.length === 0 && <div className="text-sm text-slate-500">No cases yet.</div>}
        {cases.map((c) => (
          <Link key={c.id} to={`/cases/${c.id}`} className="pm-card pm-card-hover p-5 block" data-testid={`case-card-${c.id}`}>
            <div className="flex items-center gap-3 mb-1">
              <span className={`pm-pill ${PRIORITY_TONE[c.priority]}`}>{c.priority}</span>
              <span className={`pm-pill ${STATUS_TONE[c.status]}`}>{c.status}</span>
              <span className="pm-pill pm-pill-slate">{(c.case_type || "general").replace("_", " ")}</span>
            </div>
            <div className="font-display font-bold text-lg tracking-tight">{c.title}</div>
            <div className="text-sm text-slate-500 mt-1">{(c.parties || []).join(" · ")}</div>
          </Link>
        ))}
      </div>

      {(legalCases || []).length > 0 && (
        <div className="space-y-3" data-testid="cases-tab-legal-section">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-xl font-bold tracking-tight">Legal cases ({legalCases.length})</h3>
            <Link to="/legal" className="text-xs font-bold text-emerald-700 hover:text-emerald-900">Open Legal hub →</Link>
          </div>
          {legalCases.map((lc) => (
            <Link
              key={lc.id}
              to="/legal"
              className="pm-card pm-card-hover p-5 block"
              data-testid={`legal-case-card-${lc.id}`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <span className="font-mono-tab text-xs text-slate-500">{lc.case_number}</span>
                <span className={`pm-pill ${
                  lc.status === "resolved" ? "pm-pill-green" :
                  lc.status === "hearing_scheduled" || lc.status === "appealed" ? "pm-pill-red" :
                  lc.status === "awaiting_ruling" ? "pm-pill-amber" :
                  "pm-pill-slate"
                }`}>{(lc.status || "draft").replace(/_/g, " ")}</span>
                <span className="pm-pill pm-pill-slate">{(lc.case_type || "regulatory").replace(/_/g, " ")}</span>
              </div>
              <div className="font-display font-bold text-lg tracking-tight">{lc.summary || "—"}</div>
              <div className="text-sm text-slate-500 mt-1">
                {lc.jurisdiction || "—"} · vs {lc.opposing_party || "—"} · counsel: {lc.counsel || "—"} · {lc.governing_body || "—"}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------- Schedule tab ----------------- */
function ScheduleTab({ events }) {
  return (
    <div className="pm-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl font-bold tracking-tight">Calendar ({events.length})</h3>
        <button className="pm-btn-primary h-10 px-4 rounded-xl text-sm flex items-center gap-2" data-testid="add-event-btn">
          <Plus size={14} /> Add event
        </button>
      </div>
      <div className="space-y-2">
        {events.map((e) => (
          <div key={e.id} className="flex items-center gap-4 px-4 py-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
               data-testid={`event-row-${e.id}`}>
            <div className="font-mono-tab text-sm text-slate-600 w-40 shrink-0">{fmtDateTime(e.start)}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 truncate">{e.title}</div>
              <div className="text-xs text-slate-500 truncate">{e.location}</div>
            </div>
            <span className="pm-pill pm-pill-blue">{(e.event_type || "event").replace("_", " ")}</span>
          </div>
        ))}
        {events.length === 0 && <div className="text-sm text-slate-500">No events yet.</div>}
      </div>
    </div>
  );
}

/* ----------------- Payroll tab ----------------- */
function PayrollTab({ payroll, summary, currency }) {
  return (
    <div className="space-y-5">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="pm-card p-5"><Metric label="Gross (career)" value={fmtMoney(summary.total_gross_eur, currency)} /></div>
          <div className="pm-card p-5"><Metric label="Net (career)" value={fmtMoney(summary.total_net_eur, currency)} /></div>
          <div className="pm-card p-5"><Metric label="Agent commission" value={fmtMoney(summary.total_commission, currency)} /></div>
          <div className="pm-card p-5"><Metric label="Bonuses earned" value={fmtMoney(summary.total_bonuses, currency)} /></div>
        </div>
      )}
      <div className="pm-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold tracking-tight">Monthly ledger</h3>
          <div className="flex items-center gap-2">
            <button className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold" data-testid="export-csv-btn">Export CSV</button>
            <button className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold" data-testid="export-pdf-btn">Export PDF</button>
          </div>
        </div>
        <div className="overflow-x-auto pm-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="py-2 font-bold">Period</th>
                <th className="py-2 font-bold text-right">Gross/wk</th>
                <th className="py-2 font-bold text-right">Net/wk</th>
                <th className="py-2 font-bold text-right">Signing</th>
                <th className="py-2 font-bold text-right">Loyalty</th>
                <th className="py-2 font-bold text-right">Apps</th>
                <th className="py-2 font-bold text-right">Goals</th>
                <th className="py-2 font-bold text-right">Commission</th>
              </tr>
            </thead>
            <tbody className="font-mono-tab">
              {payroll.slice().reverse().map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors" data-testid={`payroll-row-${p.id}`}>
                  <td className="py-2.5">{p.period_start?.slice(0, 7)}</td>
                  <td className="py-2.5 text-right">{fmtMoney(p.gross_weekly, p.currency)}</td>
                  <td className="py-2.5 text-right">{fmtMoney(p.net_weekly, p.currency)}</td>
                  <td className="py-2.5 text-right">{fmtMoney(p.signing_fee, p.currency)}</td>
                  <td className="py-2.5 text-right">{fmtMoney(p.loyalty_bonus, p.currency)}</td>
                  <td className="py-2.5 text-right">{fmtMoney(p.appearance_bonus, p.currency)}</td>
                  <td className="py-2.5 text-right">{fmtMoney(p.goal_bonus, p.currency)}</td>
                  <td className="py-2.5 text-right text-emerald-700 font-bold">{fmtMoney(p.agent_commission, p.currency)}</td>
                </tr>
              ))}
              {payroll.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-sm text-slate-500">No payroll entries yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function AssignedAgentChip({ clientId, client, onReassigned }) {
  const [me, setMe] = useState(null);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    cachedGet("/auth/me", 5 * 60000).then((d) => setMe(d)).catch(() => {});
    cachedGet("/workspaces/mine", 5 * 60000).then((data) => {
      const stored = localStorage.getItem("pm_active_workspace_id");
      const found = (data || []).find((w) => w.id === stored);
      const primary = (data || []).find((w) => w.is_primary) || (data || [])[0];
      const a = found || primary;
      setActive(a || null);
      if (a?.id) api.get(`/workspaces/${a.id}/members`).then((mr) => setMembers(mr.data?.members || [])).catch(() => {});
    }).catch(() => {});
  }, []);

  const wsId = client?.workspace_id || active?.id;
  const isAdmin = active?.my_role === "admin";
  const assigned = members.find((m) => m.agent_id === client?.assigned_agent_id);
  const isMine = client?.assigned_agent_id && client.assigned_agent_id === me?.id;

  const reassign = async (agentId) => {
    if (!wsId || !agentId) return;
    setSaving(true);
    try {
      await api.patch(`/workspaces/${wsId}/clients/${clientId}/assign`, { assigned_agent_id: agentId });
      setOpen(false);
      onReassigned && onReassigned();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Could not reassign client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="assigned-agent-chip">
      <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Assigned to</span>
      <div className="relative">
        <button
          type="button"
          onClick={() => isAdmin && setOpen((v) => !v)}
          data-testid="assigned-agent-pill"
          disabled={!isAdmin}
          className={`h-7 px-2.5 rounded-full border text-[12px] font-bold flex items-center gap-1.5 transition-colors ${
            client?.assigned_agent_id
              ? (isMine
                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                  : "bg-slate-100 text-slate-800 border-slate-200")
              : "bg-amber-50 text-amber-800 border-amber-200"
          } ${isAdmin ? "cursor-pointer hover:shadow-sm" : "cursor-default opacity-90"}`}
        >
          <span className="w-4 h-4 rounded-full bg-white/60 text-[9px] flex items-center justify-center font-mono-tab">
            {(assigned?.agent_name || "?").charAt(0)}
          </span>
          {assigned?.agent_name || (client?.assigned_agent_id ? "Teammate" : "Unassigned")}
          {isMine ? " (you)" : ""}
          {isAdmin && <ChevronRight size={10} className={`transition-transform ${open ? "rotate-90" : ""}`} />}
        </button>
        {isAdmin && open && (
          <div className="absolute left-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-xl shadow-xl w-60 overflow-hidden" data-testid="reassign-menu">
            <div className="px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 border-b border-slate-100 bg-slate-50">
              Reassign to
            </div>
            <div className="max-h-60 overflow-y-auto pm-scroll">
              {members.map((m) => (
                <button
                  key={m.agent_id}
                  onClick={() => reassign(m.agent_id)}
                  disabled={saving}
                  data-testid={`reassign-to-${m.agent_id}`}
                  className={`w-full px-3 py-2 flex items-center gap-2 hover:bg-slate-50 text-left transition-colors ${m.agent_id === client?.assigned_agent_id ? "bg-emerald-50/40" : ""}`}
                >
                  <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-[10px] font-bold shrink-0">
                    {(m.agent_name || "?").split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{m.agent_name}</div>
                    <div className="text-[11px] text-slate-500 capitalize">{m.role} · {m.client_count ?? 0} clients</div>
                  </div>
                </button>
              ))}
              {members.length === 0 && (
                <div className="px-3 py-4 text-center text-xs text-slate-500">No teammates in this workspace yet.</div>
              )}
            </div>
          </div>
        )}
      </div>
      {!isAdmin && client?.assigned_agent_id && !isMine && (
        <span className="text-[11px] text-slate-500">Ask an admin to reassign</span>
      )}
    </div>
  );
}


function TierStatusBanner({ client }) {
  const tier = client?.tier || "client";
  if (tier === "client") {
    return (
      <div className="mt-3 flex items-center gap-2" data-testid="tier-status-banner-client">
        <TierBadge tier="client" size="lg" showIcon />
        <span className="text-[12px] text-slate-600">Represented by your agency — full profile, contracts, cases & legal are active.</span>
      </div>
    );
  }
  if (tier === "network") {
    return (
      <div className="mt-3 p-3 rounded-xl border border-blue-200 bg-blue-50/40 flex items-start gap-3" data-testid="tier-status-banner-network">
        <TierBadge tier="network" size="lg" showIcon />
        <div className="flex-1 min-w-0 text-[12px] text-slate-700 leading-snug">
          <div className="font-bold text-slate-900">Network player · available for introduction</div>
          <div>No signed representation. Contracts, Cases and Payroll are not shown.{client?.relationship_notes ? ` Note: ${client.relationship_notes}` : ""}</div>
        </div>
      </div>
    );
  }
  // mandated
  return (
    <div className="mt-3 p-3 rounded-xl border border-amber-200 bg-amber-50/40 flex items-start gap-3" data-testid="tier-status-banner-mandated">
      <TierBadge tier="mandated" size="lg" showIcon />
      <div className="flex-1 min-w-0 text-[12px] text-slate-700 leading-snug">
        <div className="font-bold text-slate-900">Mandated — in collaboration with partner agency</div>
        <div>This player is represented by another agent; we have a mandate to place them within a defined scope. Mandate details live in the Mandates section below.</div>
      </div>
    </div>
  );
}

function ClientMandateSection({ client }) {
  const [mandates, setMandates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const tier = client?.tier || "client";
  // Only Tier 1 and Tier 3 surface a mandate section. Tier 2 (network) hides.
  const visible = tier === "client" || tier === "mandated";
  const direction = tier === "client" ? "outgoing" : "incoming";

  const load = async () => {
    if (!visible) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/mandates?client_id=${client.id}&direction=${direction}`);
      setMandates(data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client?.id, tier]);

  if (!visible) return null;

  return (
    <div className="mt-5 pm-card overflow-hidden" data-testid="client-mandate-section">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${direction === "outgoing" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          <KeyIcon size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{direction} mandate{tier === "client" ? "s" : ""}</div>
          <div className="font-display text-base font-bold tracking-tight">
            {tier === "client" ? "Permission you gave to partner agents" : "Mandate from partner agency"}
          </div>
        </div>
        {tier === "client" && (
          <button
            onClick={() => setWizardOpen(true)}
            data-testid="client-add-mandate-btn"
            className="pm-btn-primary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5"
          >
            <PlusIcon size={11} /> Add
          </button>
        )}
        {tier === "mandated" && mandates.length === 0 && !loading && (
          <button
            onClick={() => setWizardOpen(true)}
            data-testid="client-add-mandate-btn"
            className="pm-btn-primary h-9 px-3 rounded-lg text-[12px] font-bold flex items-center gap-1.5"
          >
            <PlusIcon size={11} /> Attach mandate
          </button>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {loading ? (
          <div className="p-4 text-sm text-slate-500">Loading…</div>
        ) : mandates.length === 0 ? (
          <div className="p-5 text-center text-sm text-slate-500">
            No {direction} mandates yet.
            {tier === "mandated" && (
              <div className="mt-1 text-[12px] text-amber-700">Attach the signed mandate to make this player audit-ready.</div>
            )}
          </div>
        ) : (
          mandates.map((m) => (
            <button
              key={m.id}
              onClick={() => setDetailId(m.id)}
              data-testid={`client-mandate-row-${m.id}`}
              className="w-full text-left px-5 py-3 hover:bg-slate-50 transition-colors flex items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-slate-900 truncate">
                  {m.partner_agent_name}{m.partner_agency_name ? ` · ${m.partner_agency_name}` : ""}
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap text-[11px]">
                  <span className={`pm-pill capitalize ${m.status === "active" ? "pm-pill-emerald" : m.status === "expired" ? "pm-pill-red" : "pm-pill-slate"}`}>
                    {m.status}
                  </span>
                  <span className="text-slate-500">
                    Scope: <b className="text-slate-700">{(m.scope_clubs?.length ? m.scope_clubs.join(", ") : null) || (m.scope_leagues?.length ? m.scope_leagues.join(", ") : null) || m.scope_description || m.scope_type}</b>
                  </span>
                  {m.expiry_date && <span className="text-slate-500">· expires {fmtDate(m.expiry_date)}</span>}
                  {m.commission_split_pct != null && <span className="text-slate-500">· {m.commission_split_pct}% split</span>}
                </div>
              </div>
              <ChevronRight size={13} className="text-slate-300 shrink-0" />
            </button>
          ))
        )}
      </div>

      {wizardOpen && (
        <MandateWizard
          direction={direction}
          onClose={() => setWizardOpen(false)}
          onCreated={() => { setWizardOpen(false); load(); }}
        />
      )}
      {detailId && (
        <MandateDetailDrawer
          mandateId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}


function ClientReportsSection({ clientId, clientName }) {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerMode, setPickerMode] = useState(null);     // "client" | "inbox" | null
  const [pickerReport, setPickerReport] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/reports?client_id=${clientId}`)
      .then((r) => setReports(r.data || []))
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [clientId]);

  const downloadReport = async (rep) => {
    const token = localStorage.getItem("pm_token");
    const res = await fetch(`${API}/reports/${rep.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = rep.filename || `report-${rep.id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const openBuilder = () => window.open(`/reports`, "_blank", "noopener");

  return (
    <div className="mt-4 pm-card p-5" data-testid="client-reports-section">
      <div className="flex items-center gap-2 mb-3">
        <FileText size={14} className="text-emerald-600" />
        <h4 className="font-bold text-sm tracking-tight text-slate-800">Player reports</h4>
        <span className="text-[10px] text-slate-500 ml-1">Last {Math.min(reports.length, 3)} of {reports.length}</span>
        <button onClick={openBuilder}
          className="ml-auto pm-btn-secondary h-7 px-2.5 rounded-lg text-[10px] font-bold flex items-center gap-1"
          data-testid="client-generate-report-btn">
          <Plus size={10} /> Generate report
        </button>
      </div>
      {loading && <div className="text-[12px] text-slate-500">Loading reports…</div>}
      {!loading && reports.length === 0 && (
        <div className="text-[12px] text-slate-500">
          No reports yet. Click <b>Generate report</b> to build a Player Profile in the report builder.
        </div>
      )}
      {!loading && reports.length > 0 && (
        <div className="space-y-1.5">
          {reports.slice(0, 3).map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-200 hover:border-emerald-300"
                 data-testid={`client-report-${r.id}`}>
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                <FileText size={12} className="text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">{r.title || r.client_name || "Untitled"}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-2">
                  <span className="capitalize">{(r.report_type || "").replace("_", " ")}</span>
                  <span>·</span>
                  <span className="capitalize">{r.theme}</span>
                  <span>·</span>
                  <span>{r.page_count}p</span>
                  <span>·</span>
                  <span>{fmtDateTime(r.created_at).slice(0, 11)}</span>
                </div>
              </div>
              <button onClick={() => downloadReport(r)}
                className="pm-btn-secondary h-7 px-2 rounded-md text-[10px] font-bold flex items-center gap-1"
                data-testid={`client-report-download-${r.id}`}>
                <Download size={10} /> PDF
              </button>
              <button onClick={() => { setPickerReport(r); setPickerMode("inbox"); }}
                className="pm-btn-primary h-7 px-2 rounded-md text-[10px] font-bold flex items-center gap-1"
                data-testid={`client-report-resend-${r.id}`}>
                <Send size={10} /> Resend
              </button>
            </div>
          ))}
        </div>
      )}
      {pickerMode && (
        <ReportPickerModal
          mode="inbox"
          clientId={clientId}
          defaultRecipient=""
          defaultChannel="email"
          defaultSubject={`${clientName} — Playmaker report`}
          onClose={() => { setPickerMode(null); setPickerReport(null); }}
          onSuccess={() => { setPickerMode(null); setPickerReport(null); load(); }}
        />
      )}
    </div>
  );
}
